import { X, Image, Edit2 } from 'lucide-react';
import TaskCategoryDropdown from './TaskCategoryDropdown';

/**
 * Shared modal for:
 *  - mode="upload" : preview multiple pending files, select task category, then upload
 *  - mode="edit"   : preview single existing image, change its task category
 */
const MediaModal = ({
  mode,
  accent,
  tasks,
  pendingFiles, // Upload-mode props
  imageUrl, // Edit-mode props
  // Shared props
  taskValue,
  onTaskChange,
  onClose,
  onConfirm,
  isLoading,
  onDelete, // Optional delete handler for edit mode
}) => {
  const isEdit       = mode === 'edit';
  const title        = isEdit ? 'Edit Kategori Foto' : 'Upload Documentation';
  const TitleIcon    = isEdit ? Edit2 : Image;
  const confirmLabel = isEdit ? 'Simpan Perubahan' : 'Simpan';
  const labelSelect  = isEdit ? 'Pilih Kategori Task Baru' : 'Pilih Kategori Task';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-900 backdrop-blur-sm"
        style={{ background: 'rgba(15, 23, 42, 0.4)', animation: 'fadeIn 0.2s ease' }}
        onClick={onClose}
      />

      {/* Modal Container */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-901 bg-white rounded-2xl w-[90%] max-w-110 p-6"
        style={{ boxShadow: '0 20px 40px rgba(0,0,0,0.15)', animation: 'fadeSlideUp 0.3s ease' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="m-0 text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <TitleIcon size={18} color={accent} />
            {title}
          </h3>
          {isEdit && (
            <button
              onClick={onClose}
              className="flex items-center justify-center p-1 rounded-full border-none bg-transparent cursor-pointer transition-colors duration-200 hover:bg-slate-100"
            >
              <X size={20} color="#64748b" />
            </button>
          )}
        </div>

        {/* Preview */}
        {isEdit ? (
          <div className="w-full h-48 rounded-xl overflow-hidden mb-5 border border-slate-200">
            <img
              src={imageUrl}
              alt="preview"
              className="w-full h-full object-contain bg-slate-50"
            />
          </div>
        ) : (
          <div className="flex gap-2.5 overflow-x-auto pb-3 mb-4">
            {pendingFiles.map((pf, idx) => (
              <div key={idx} className="w-20 h-20 rounded-xl overflow-hidden shrink-0 border border-slate-200">
                <img src={pf.url} alt="preview" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Task Category Dropdown */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-slate-500 mb-2">
            {labelSelect}
          </label>
          <TaskCategoryDropdown
            value={taskValue}
            onChange={onTaskChange}
            tasks={tasks}
            accent={accent}
          />
        </div>

        {/* Actions */}
        <div className={`flex ${isEdit ? 'justify-between' : 'justify-end'} gap-2.5`}>
          <div className="flex gap-1">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-500 text-sm font-semibold transition-colors duration-200 hover:bg-slate-50"
              style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={onDelete}
                disabled={isLoading}
                className="px-4 py-2.5 rounded-xl border border-red-300 bg-red-100 text-red-500 text-sm font-semibold transition-colors duration-200 hover:bg-red-200"
                style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                Delete
              </button>
            )}
          </div>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-5 py-2.5 rounded-xl border-none text-white text-sm font-bold transition-all duration-200"
            style={{
              background: isLoading ? '#94a3b8' : accent,
              boxShadow: isLoading ? 'none' : `0 4px 12px ${accent}40`,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Menyimpan...' : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default MediaModal;
